import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Stack,
} from '@mui/material';
import { getRecipeEmoji } from '../../utils/getRecipeEmoji';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * ShoppingList - Liste de courses
 *
 * V3.0: Mobile-First Refactoring
 * - Correction bug unite (plus de "g g")
 * - Grid responsive (2 cols mobile, auto-fit desktop)
 * - Checkbox au lieu de Switch (plus intuitif)
 * - Tri alphabetique
 */
const ShoppingList = ({ shoppingList }) => {
  const [checkedItems, setCheckedItems] = useState(() => {
    const saved = localStorage.getItem('shoppingListChecked');
    return saved ? JSON.parse(saved) : [];
  });

  const [filter, setFilter] = useState('toBuy');

  const toggleItem = (ingredient) => {
    setCheckedItems((prev) => {
      const updated = prev.includes(ingredient)
        ? prev.filter((item) => item !== ingredient)
        : [...prev, ingredient];

      localStorage.setItem('shoppingListChecked', JSON.stringify(updated));
      return updated;
    });
  };

  // Tri alphabetique
  const sortedList = [...shoppingList].sort((a, b) =>
    (a.ingredient || '').localeCompare(b.ingredient || '')
  );

  // Filtrage
  const filteredList = sortedList.filter((item) => {
    if (filter === 'toBuy') {
      return !checkedItems.includes(item.ingredient);
    } else if (filter === 'checked') {
      return checkedItems.includes(item.ingredient);
    }
    return true;
  });

  // Formatage de la quantite avec unite correcte
  const formatQuantity = (item) => {
    const qty = Math.round(item.quantity || 0);
    const unit = item.unit || 'g';
    return `${qty} ${unit}`;
  };

  if (!shoppingList || shoppingList.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">Aucun ingredient dans la liste</Typography>
      </Box>
    );
  }

  const toBuyCount = shoppingList.filter((item) => !checkedItems.includes(item.ingredient)).length;
  const checkedCount = checkedItems.length;

  return (
    <Box>
      {/* Header avec compteurs */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Liste de courses
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {toBuyCount} a acheter
        </Typography>
      </Stack>

      {/* Filtres */}
      <ToggleButtonGroup
        value={filter}
        exclusive
        onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
        sx={{
          width: '100%',
          mb: 3,
          '& .MuiToggleButton-root': {
            flex: 1,
            textTransform: 'none',
            fontWeight: 500,
            py: 1,
          },
        }}
      >
        <ToggleButton value="toBuy">
          <ShoppingCartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          A acheter ({toBuyCount})
        </ToggleButton>
        <ToggleButton value="checked">
          <CheckCircleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          Achete ({checkedCount})
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Liste des ingredients */}
      {filteredList.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {filter === 'toBuy' ? 'Tout est achete !' : 'Aucun article achete'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(auto-fit, minmax(160px, 1fr))',
            },
          }}
        >
          {filteredList.map((item, index) => {
            const isChecked = checkedItems.includes(item.ingredient);

            return (
              <Card
                key={index}
                onClick={() => toggleItem(item.ingredient)}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isChecked ? 'success.main' : 'divider',
                  boxShadow: 'none',
                  bgcolor: isChecked ? 'success.light' : 'background.paper',
                  opacity: isChecked ? 0.7 : 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Checkbox
                      checked={isChecked}
                      size="small"
                      sx={{ p: 0, mt: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleItem(item.ingredient)}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '1rem',
                            bgcolor: 'grey.100',
                          }}
                        >
                          {getRecipeEmoji(item.ingredient)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            textDecoration: isChecked ? 'line-through' : 'none',
                          }}
                        >
                          {formatQuantity(item)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textDecoration: isChecked ? 'line-through' : 'none',
                        }}
                      >
                        {item.ingredient}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ShoppingList;
